import React, { Component } from 'react';
import { Link } from 'react-router';
import discountApi from '../../components/discounts/discountApi';
import SimulationResult from '../../components/discounts/SimulationResult';
import { previousMonthPeriod } from '../../utils/discountFormat';

/**
 * OPE-112 "Simulate billing": runs the real invoice generation for a period
 * through POST billing/simulations (rolled back, nothing persists, no
 * emails or syncs) and shows lines, segments, winning discounts and the
 * legacy-vs-new diff. Up to 25 clients; the whole book stays on
 * `php artisan billing:simulate`.
 */
export default class Simulate extends Component {
    constructor(props) {
        super(props);
        var period = previousMonthPeriod();
        this.state = {
            start: period.start,
            end: period.end,
            engine: 'both',
            compareActual: false,
            clients: [],
            search: '',
            isSearching: false,
            searched: false,
            hits: [],
            report: null,
            isRunning: false,
            error: null,
            runs: null,
            openedRun: null
        };
    }

    componentDidMount() {
        document.title = 'Simulate billing | Fergus CRM';
        this.loadRuns();
    }

    loadRuns() {
        discountApi.listSimulations()
            .then(runs => this.setState({ runs }))
            .catch(() => this.setState({ runs: [] }));
    }

    openRun(run) {
        this.setState({ isRunning: true, error: null });
        discountApi.getSimulation(run.billing_simulation_run_id)
            // Restore the saved cohort with the other inputs, so what is shown
            // and what "Run again" submits are the same clients.
            .then(full => this.setState({ report: full.report, openedRun: full, isRunning: false, start: full.period_start, end: full.period_end, engine: full.engine, compareActual: full.compare_actual, clients: full.clients || [], hits: [], search: '' }))
            .catch(error => this.setState({ error: error.message, isRunning: false }));
    }

    componentWillUnmount() {
        document.title = 'Fergus CRM';
    }

    search(e) {
        if (e) { e.preventDefault(); }
        if (this.state.search.trim().length < 2) {
            this.setState({ error: 'Type at least two characters of the client name to search.' });
            return;
        }
        this.setState({ isSearching: true, error: null });
        discountApi.searchClients(this.state.search)
            .then(hits => this.setState({ hits, isSearching: false, searched: true }))
            .catch(error => this.setState({ error: error.message, isSearching: false }));
    }

    addClient(hit) {
        // Results stay listed so several clients can be added from one search
        if (!this.state.clients.some(c => c.id === hit.id) && this.state.clients.length < 25) {
            this.setState({ clients: this.state.clients.concat([hit]) });
        }
    }

    run() {
        this.setState({ isRunning: true, error: null, report: null });
        discountApi.simulate({
            start: this.state.start,
            end: this.state.end,
            client_ids: this.state.clients.map(c => c.id),
            engine: this.state.engine,
            compare_actual: this.state.compareActual
        }).then(report => {
            this.setState({ report, openedRun: null, isRunning: false });
            this.loadRuns();
        }).catch(error => this.setState({ error: error.message, isRunning: false }));
    }

    render() {
        var s = this.state;
        return (
            <div className="row page-todo">
                <ol className="breadcrumb page-breadcrumb">
                    <li><Link to="/discounts">Discounts</Link></li>
                    <li className="active">Simulate billing</li>
                </ol>
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-heading"><h2 style={{ margin: 0 }}><i className="fa fa-flask"></i> Simulate billing</h2></div>
                        <div className="panel-body">
                            <p className="text-muted">Runs the real invoice generation for the period and rolls it back. Nothing is written, no emails or syncs fire. Up to 25 clients per run; use <code>billing:simulate</code> for the whole book.</p>
                            <form className="form-inline" onSubmit={this.search.bind(this)}>
                                <div className="form-group" style={{ marginRight: 10 }}>
                                    <label style={{ marginRight: 5 }}>Period</label>
                                    <input type="date" className="form-control input-sm" value={s.start} onChange={e => this.setState({ start: e.target.value })} />
                                    {' '}to{' '}
                                    <input type="date" className="form-control input-sm" value={s.end} onChange={e => this.setState({ end: e.target.value })} />
                                    <small className="text-muted" style={{ marginLeft: 5 }}>end exclusive, UTC</small>
                                </div>
                                <div className="form-group" style={{ marginRight: 10 }}>
                                    <label style={{ marginRight: 5 }}>Engine</label>
                                    <select className="form-control input-sm" value={s.engine} onChange={e => this.setState({ engine: e.target.value })}>
                                        <option value="both">both (diff legacy vs new)</option>
                                        <option value="new">new engine only</option>
                                        <option value="legacy">legacy only</option>
                                    </select>
                                </div>
                                <div className="checkbox" style={{ marginRight: 10 }}>
                                    <label><input type="checkbox" checked={s.compareActual} onChange={e => this.setState({ compareActual: e.target.checked })} /> compare with the real invoice</label>
                                </div>
                            </form>
                            <form style={{ marginTop: 10 }} onSubmit={this.search.bind(this)}>
                                <label>Add clients</label>
                                <div className="row">
                                    <div className="col-xs-6">
                                        <div className="input-group input-group-sm">
                                            <input className="form-control" placeholder="search by client name" value={s.search} onChange={e => this.setState({ search: e.target.value })} />
                                            <span className="input-group-btn">
                                                <button type="submit" className="btn btn-primary" disabled={s.isSearching}>
                                                    <i className="fa fa-search"></i> {s.isSearching ? 'Searching...' : 'Search'}
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-xs-6 text-right">
                                        <button type="button" className="btn btn-sm btn-info" disabled={s.clients.length === 0 || s.isRunning} onClick={this.run.bind(this)}>
                                            {s.isRunning ? 'Simulating...' : `Simulate ${s.clients.length} client${s.clients.length === 1 ? '' : 's'}`}
                                        </button>
                                    </div>
                                </div>
                            </form>
                            <div className="row" style={{ marginTop: 10 }}>
                                <div className="col-xs-6">
                                    {s.searched && s.hits.length === 0 && <p className="text-muted"><small>No clients match "{s.search}".</small></p>}
                                    {s.hits.length > 0 &&
                                        <div>
                                            <small className="text-muted">Search results — click to add</small>
                                            <div className="list-group" style={{ marginTop: 4, marginBottom: 0, maxHeight: 260, overflowY: 'auto' }}>
                                                {s.hits.map(h => {
                                                    var added = s.clients.some(c => c.id === h.id);
                                                    return (
                                                        <a href="#" key={h.id} className={`list-group-item ${added ? 'disabled' : ''}`} onClick={e => { e.preventDefault(); if (!added) { this.addClient(h); } }}>
                                                            <span className="pull-right text-muted"><small>{added ? 'added' : <i className="fa fa-plus"></i>}</small></span>
                                                            {h.name} <small className="text-muted">{h.country || ''}</small>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    }
                                </div>
                                <div className="col-xs-6">
                                    {s.clients.length > 0 &&
                                        <div>
                                            <small className="text-muted">Selected ({s.clients.length} of 25)</small>
                                            <table className="table table-condensed" style={{ marginTop: 4, marginBottom: 0 }}>
                                                <tbody>
                                                    {s.clients.map(c =>
                                                        <tr key={c.id}>
                                                            <td>{c.name} <small className="text-muted">{c.country || ''}</small></td>
                                                            <td className="text-right" style={{ width: 60 }}>
                                                                <button type="button" className="btn btn-xs btn-default" onClick={() => this.setState({ clients: s.clients.filter(x => x.id !== c.id) })}>Remove</button>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    }
                                </div>
                            </div>
                            {s.error && <div className="alert alert-danger" style={{ marginTop: 10 }}>{s.error}</div>}
                        </div>
                    </div>

                    {s.openedRun &&
                        <div className="alert alert-info">
                            Showing a stored run from {new Date(s.openedRun.created_at).toLocaleString()}{s.openedRun.created_by_name ? ` by ${s.openedRun.created_by_name}` : ''}. Run again for current data.
                        </div>
                    }
                    {s.report &&
                        <div className="panel panel-default">
                            <div className="panel-body">
                                {Object.keys(s.report.summary).map(k =>
                                    <span key={k} style={{ marginRight: 20 }}>
                                        <span className="text-muted">{k.replace(/_/g, ' ')}</span>{' '}
                                        <strong className={(k === 'unexplained' || k === 'errors') && s.report.summary[k] > 0 ? 'text-danger' : ''}>{s.report.summary[k]}</strong>
                                    </span>
                                )}
                            </div>
                        </div>
                    }
                    {s.report && s.report.clients.map(c => <SimulationResult key={c.client_id} result={c} />)}

                    <div className="panel panel-default">
                        <div className="panel-heading"><h2 style={{ margin: 0 }}><i className="fa fa-history"></i> Previous runs</h2></div>
                        <div className="panel-body no-padding">
                            {s.runs === null && <p className="text-muted" style={{ padding: 10 }}>Loading...</p>}
                            {s.runs && s.runs.length === 0 && <p className="text-muted" style={{ padding: 10 }}>No stored runs yet. Runs from this screen are kept so they can be reopened.</p>}
                            {s.runs && s.runs.length > 0 &&
                                <table className="table table-condensed table-hover" style={{ marginBottom: 0 }}>
                                    <thead><tr><th>When</th><th>By</th><th>Period</th><th>Engine</th><th>Clients</th><th className="text-right">Unexplained</th><th className="text-right">Errors</th><th></th></tr></thead>
                                    <tbody>
                                        {s.runs.map(r =>
                                            <tr key={r.billing_simulation_run_id} className={s.openedRun && s.openedRun.billing_simulation_run_id === r.billing_simulation_run_id ? 'info' : ''}>
                                                <td>{new Date(r.created_at).toLocaleString()}</td>
                                                <td>{r.created_by_name || <span className="text-muted">–</span>}</td>
                                                <td>{r.period_start} – {r.period_end}</td>
                                                <td>{r.engine}{r.compare_actual ? ' + actual' : ''}</td>
                                                <td title={(r.client_names || []).join(', ')}>{r.client_count}: {(r.client_names || []).slice(0, 3).join(', ')}{(r.client_names || []).length > 3 ? '…' : ''}</td>
                                                <td className={`text-right ${r.summary && r.summary.unexplained > 0 ? 'text-danger' : ''}`}>{r.summary ? r.summary.unexplained : '–'}</td>
                                                <td className={`text-right ${r.summary && r.summary.errors > 0 ? 'text-danger' : ''}`}>{r.summary ? r.summary.errors : '–'}</td>
                                                <td className="text-right"><button type="button" className="btn btn-xs btn-default" disabled={s.isRunning} onClick={() => this.openRun(r)}>Open</button></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./app/pages/discounts/Simulate.js